import type { MapRef } from "@vis.gl/react-maplibre";
import { useMemo, useRef, useState, type FC } from "react";

import {
  MarkerIconType,
  routeColorsByIdMapping,
  TilesServiceEnum,
  type ICoordinate,
  type ILocationInfo,
} from "@ryde/shared";

import {
  MapLibreMapHost,
  MapLibreStationPinMarker,
  useMapLibreLocationsAutoFit,
} from "./maplibre";
import {
  DEFAULT_ZOOM,
  STATIC_MAP_MARKER_COLOR_ID,
} from "./static-map.constants";
import { StaticMapContainerStyled } from "./static-map.styled";

interface IStaticMapProps {
  coordinate?: ICoordinate;
  height?: string;
  width?: string;
  zoom?: number;
  isDraggingAllowed?: boolean;
  isZoomAllowed?: boolean;
}

/**
 * Small non-interactive map showing one coordinate — the preview under the quick-add-station forms.
 *
 * Pinned to OpenStreetMap via `forcedTilesService`, as the Leaflet version was: it hardcoded the OSM tile
 * layer and ignored its own `provider` prop, which no call site ever passed. That prop is gone along with
 * the Google Static Maps image branch it gated; the branch was unreachable for the same reason.
 *
 * The pin is deliberate, not incidental — without it the shared host would resolve the company's
 * `tilesService`, which would silently switch this preview to Google/Mapbox tiles for some companies.
 * Letting it follow the company provider is a visible change, so it is not being made here.
 */
export const StaticMap: FC<IStaticMapProps> = ({
  coordinate,
  isDraggingAllowed = false,
  isZoomAllowed = false,
  zoom = DEFAULT_ZOOM,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const locations = useMemo<ILocationInfo[]>(
    () =>
      coordinate
        ? [{ latitude: coordinate.lat, longitude: coordinate.lng }]
        : [],
    [coordinate],
  );

  useMapLibreLocationsAutoFit({ mapRef, locations, isMapLoaded });

  if (!coordinate) {
    return null;
  }

  return (
    <StaticMapContainerStyled className="static-map">
      <MapLibreMapHost
        mapRef={mapRef}
        onLoadedChange={setIsMapLoaded}
        initialCenter={coordinate}
        initialZoom={zoom}
        isZoomEnabled={isZoomAllowed}
        isDragPanEnabled={isDraggingAllowed}
        areControlsHidden={!isZoomAllowed}
        forcedTilesService={TilesServiceEnum.OSM}
        isLayerControlHidden
        isAttributionHidden
        isPlaceInfoHidden
      >
        {isMapLoaded && (
          <MapLibreStationPinMarker
            latitude={coordinate.lat}
            longitude={coordinate.lng}
            type={MarkerIconType.Point}
            color={routeColorsByIdMapping[STATIC_MAP_MARKER_COLOR_ID]}
          />
        )}
      </MapLibreMapHost>
    </StaticMapContainerStyled>
  );
};
